import { Pagination } from "app/pages/component/Pagination";
import React, { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { Input } from "sr/helpers";
import { getUserDetails } from "sr/helpers/functions/getUserDetails";
import { HandleApiError } from "sr/helpers/functions/HandleApiError";
import { Button } from "sr/helpers/ui-components/company/Button";
import { ReactSelect } from "sr/helpers/ui-components/ReactSelect";
import { addConJobType } from "sr/utils/api/company/contractor/addConJobType";
import { getConJobType } from "sr/utils/api/company/contractor/getConJobType";
import { updateConJobType } from "sr/utils/api/company/contractor/updateConJobType";
import ContentWithSpinner from "sr/layout/common/ContentWithSpiner";
import { LoaderFullScreen } from "sr/layout/common/LoaderFullScreen";
import { deleteConJobType } from "sr/utils/api/company/contractor/deleteConJobType";
import { getJobTypeCategory } from "sr/utils/api/getJobTypeCategory";
import { toast } from "react-toastify";
import { renderRequiredLabel } from "sr/utils/helper";

export const JobType = () => {
  const [companyId, setCompanyId] = useState<string>('');
  const [contractorId, setContractorId] = useState<string>('');
  const [data, setData] = useState<any[]>([]);
  const [assignedJobTypes, setAssignedJobTypes] = useState<typeof data>([]);
  const params = useParams();
  const [loader, setLoader] = useState(false);
  const [buttonLoader, setButtonLoader] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPage, settotalPage] = useState<number>(1);
  const [resetKey, setResetKey] = useState(0);
  
  const [jobTypes, setJobTypes] = useState<any[]>([]);
  
  const [alljobs, setAllJobs] = useState(true);
  const [sameRate, setSameRate] = useState(true);

  const [jobType, setJobType] = useState<string>("");
  const [rate, setRate] = useState<string>('');


  const [multiJobType, setMultiJobType] = useState<any[]>([]);
  const [multiJobTypeErrors, setMultiJobTypeErrors] = useState<Record<string, string>>({});

  const intl = useIntl();
  const jobTypeLabel = renderRequiredLabel(
    intl.formatMessage({
      id: "CLEANER.PROFILE.JOBTYPE.TITLE",
      defaultMessage: "Job Type",
    })
  );
  const hourlyRateLabel = renderRequiredLabel(
    intl.formatMessage({
      id: "CLEANER.PROFILE.JOBTYPE.RATE",
      defaultMessage: "Hourly Rate",
    })
  );

  useEffect(() => {
    const { id } = params;
    if (id) setContractorId(id);
  }, [params]);

  useEffect(() => {
    const { user_id } = getUserDetails();
    if (user_id) setCompanyId(user_id);
  }, []);

  const limit = 10;

  const getJobTypeQueryParams = (
    cid: string,
    pageNumber: number,
    pageLimit: number
  ) => {
    const queryParams = new URLSearchParams();
    queryParams.append("contractor_id", cid);
    queryParams.append("page", pageNumber.toString());
    queryParams.append("limit", pageLimit.toString());
    return queryParams;
  };

  const loadPagedAssignedJobTypes = async (
    cid: string,
    pageNumber: number = page
  ) => {
    setLoader(true);
    try {
      const queryParams = getJobTypeQueryParams(cid, pageNumber, limit);
      const res = await getConJobType(queryParams);
      if (res?.success === true) {
        const nextData = res?.data || [];
        const nextTotalPage = Math.max(
          1,
          Math.ceil((res?.pagination?.total || 0) / limit)
        );

        setData(nextData);
        settotalPage(nextTotalPage);

        if (pageNumber > nextTotalPage) {
          setPage(nextTotalPage);
        }

        return nextData;
      }
    } catch (e) {
      HandleApiError(e);
    } finally {
      setLoader(false);
    }

    setData([]);
    settotalPage(1);
    return [];
  };

  const getAllAssignedJobTypes = async (cid: string) => {
    try {
      const queryParams = getJobTypeQueryParams(cid, 1, 1000);
      const res = await getConJobType(queryParams);
      if (res?.success === true) {
        const nextData = res?.data || [];
        setAssignedJobTypes(nextData);
        return nextData;
      }
    } catch (e) {
      HandleApiError(e);
    }

    setAssignedJobTypes([]);
    return [];
  };

  useEffect(() => {
    if (contractorId) {
      setPage(1);
    }
  }, [contractorId]);

  useEffect(() => {
    if (contractorId) {
      loadPagedAssignedJobTypes(contractorId, page);
    }
  }, [contractorId, page]);

  useEffect(() => {
    const fetchJobType = async () => {
      try {
        const res = await getJobTypeCategory();
        if (res?.success === true) {
          setJobTypes(res?.data);
        }
      } catch (e: any) {}
    };
    fetchJobType();
  }, []);

  const getFallbackJobTypeLabel = (labelId?: string, value?: string) => {
    const source = value || labelId?.split(".").pop() || "";
    return source
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getFormattedJobTypeLabel = (labelId?: string, value?: string) => {
    const fallback = getFallbackJobTypeLabel(labelId, value);
    if (!labelId) return fallback;
    return intl.formatMessage({
      id: labelId,
      defaultMessage: fallback,
    });
  };

  const options = jobTypes
    ?.map((item) => ({
      value: item?.value,
      label: getFormattedJobTypeLabel(item?.label, item?.value),
    }))
    ?.sort((a, b) => a.label.localeCompare(b.label));

  const getJobTypeLabel = (jobTypeValue: string) => {
    const matchedJobType = jobTypes?.find(
      (item) => item?.value === jobTypeValue
    );

    return getFormattedJobTypeLabel(
      matchedJobType?.label,
      matchedJobType?.value || jobTypeValue
    );
  };

  useEffect(() => {
    if (contractorId) {
      getAllAssignedJobTypes(contractorId);
    }
  }, [contractorId]);

  const handleDeleteAssignedJob = async (id: string) => {
    try {
      setButtonLoader(true);
      const res = await deleteConJobType({ id });
      if (res?.success) {
        await Promise.all([
          loadPagedAssignedJobTypes(contractorId, page),
          getAllAssignedJobTypes(contractorId),
        ]);
      }
    } catch (e) {
      toast.error("Error occurred while deleting");
    } finally {
      setButtonLoader(false);
    }
  };

  const AddAndUpdateJobType = async () => {
    setButtonLoader(true);

    const errors: Record<string, string> = {};
    let invalidFound = false;

    const toCreate: any[] = [];
    const toUpdate: any[] = [];

    let payloadItems: any[] = [];

    if (alljobs && sameRate) {
      if (!rate || Number(rate) <= 0) {
        toast.error("Please provide a valid hourly rate greater than 0");
        setButtonLoader(false);
        return;
      }
      payloadItems = options.map((opt) => ({
        contractor_id: contractorId,
        company_id: companyId,
        job_type: opt.value as string,
        rate: rate,
      }));
    } else if (alljobs && !sameRate) {
      payloadItems = options.map((opt) => {
        const matching = multiJobType.find((i) => i.job_type === opt.value);
        const existingData = assignedJobTypes?.find(
          (d) => d.job_type === opt.value
        );
        return {
          contractor_id: contractorId,
          company_id: companyId,
          job_type: opt.value as string,
          rate: matching ? matching.rate : (existingData ? existingData.rate : 0),
        };
      });
    } else {
      if (!jobType) {
        toast.error("Please select a Job Type.");
        setButtonLoader(false);
        return;
      }
      if (!rate || Number(rate) <= 0) {
        toast.error("Please provide a valid hourly rate greater than 0.");
        setButtonLoader(false);
        return;
      }

      payloadItems = [
        {
          contractor_id: contractorId,
          company_id: companyId,
          job_type: jobType,
          rate: rate,
        },
      ];
    }

    payloadItems.forEach((item) => {
      if (!item.rate || Number(item.rate) <= 0) {
        errors[item.job_type] = "Rate must be a valid number greater than 0";
        invalidFound = true;
        return;
      }

      const existing = assignedJobTypes?.find(
        (d: any) => d.job_type === item.job_type
      );
      
      if (existing) {
        // Only update if rate differs to reduce noise
        if (Number(existing.rate) !== Number(item.rate)) {
          toUpdate.push({
            ...item,
            id: existing?.id,
          });
        }
      } else {
        toCreate.push({
          ...item,
        });
      }
    });

    if (invalidFound) {
      setMultiJobTypeErrors(errors);
      if (alljobs) toast.error("Please ensure all jobs have a valid rate.");
      setButtonLoader(false);
      return;
    } else {
      setMultiJobTypeErrors({});
    }

    if (toCreate.length === 0 && toUpdate.length === 0) {
      toast.info("No modifications or new rates were detected to save.");
      setButtonLoader(false);
      return;
    }

    try {
      const updatePromises = toUpdate.map((item) => updateConJobType(item));
      const createPromises = toCreate.map((item) => addConJobType(item));

      await Promise.all([...updatePromises, ...createPromises]);
      await Promise.all([
        loadPagedAssignedJobTypes(contractorId, page),
        getAllAssignedJobTypes(contractorId),
      ]);

      if (!alljobs) {
        setJobType("");
        setRate("");
        setMultiJobType([]);
        setMultiJobTypeErrors({});
        setResetKey(prev => prev + 1);
      } else if (alljobs && !sameRate) {
        setMultiJobType([]);
      }
      
      toast.success("Job types saved successfully.");
    } catch (err) {
      toast.error("Something went wrong while saving");
    } finally {
      setButtonLoader(false);
    }
  };

  const handleRateChange = (newRate: string, jobTypeValue: string) => {
    setMultiJobType((prevItems) => {
      const existingIndex = prevItems.findIndex((i) => i.job_type === jobTypeValue);
      if (existingIndex !== -1) {
        const updatedItems = [...prevItems];
        updatedItems[existingIndex] = {
          ...updatedItems[existingIndex],
          rate: newRate,
        };
        return updatedItems;
      } else {
        return [
          ...prevItems,
          {
            contractor_id: contractorId,
            company_id: companyId,
            job_type: jobTypeValue,
            rate: newRate,
          },
        ];
      }
    });
  };

  return (
    <ContentWithSpinner loader={loader} height="h-screen">
      {buttonLoader && <LoaderFullScreen />}

      <div className="mt-5 mb-5">
        <label className={`text-normal text-sm`}>
          Assign all job types
        </label>
        <div className="flex mt-2">
          <div
            className={`cursor-pointer text-[#0078ae] border p-1 rounded-2xl w-12 text-center mr-3 ${
              alljobs ? "bg-gray-200 font-bold border-gray-600" : "border-gray-300"
            }`}
            onClick={() => setAllJobs(true)}
          >
            Yes
          </div>
          <div
            className={`cursor-pointer text-[#0078ae] border p-1 rounded-2xl w-12 text-center mr-3 ${
              !alljobs ? "bg-gray-200 font-bold border-gray-600" : "border-gray-300"
            }`}
            onClick={() => setAllJobs(false)}
          >
            No
          </div>
        </div>
      </div>

      {alljobs && (
        <div className="mt-10 mb-5">
          <label className={`text-normal text-sm`}>
            Pay same rate for all job types
          </label>
          <div className="flex mt-2">
            <div
              className={`cursor-pointer border p-1 rounded-2xl w-12 text-[#0078ae] text-center mr-3 ${
                sameRate ? "bg-gray-200 font-bold border-gray-600" : "border-gray-300"
              }`}
              onClick={() => setSameRate(true)}
            >
              Yes
            </div>
            <div
              className={`cursor-pointer text-[#0078ae] border p-1 rounded-2xl w-12 text-center mr-3 ${
                !sameRate ? "bg-gray-200 font-bold border-gray-600" : "border-gray-300"
              }`}
              onClick={() => setSameRate(false)}
            >
              No
            </div>
          </div>
        </div>
      )}

      {!alljobs && (
        <div className="mt-5 w-[350px]">
          <ReactSelect
            key={`select-jobType-${resetKey}`}
            label={jobTypeLabel}
            type="text"
            onChange={(selectedOptions: any) => setJobType(selectedOptions?.value)}
            placeholder={intl.formatMessage({
              id: "CLEANER.PROFILE.JOBTYPE.INPUT.PLACEHOLDER",
              defaultMessage: "Enter Job Type",
            })}
            value={jobType}
            options={options}
            defaultValue={options?.find((item) => item?.value === jobType)}
          />
        </div>
      )}
      
      {(sameRate || !alljobs) && (
        <div className="mt-5 w-[350px]">
          <Input
            InputDivClass=""
            topinputdiv="-mt-2"
            onChange={(e: any) => setRate(e.target.value)}
            value={rate}
            name="rate"
            label={hourlyRateLabel}
            type="number"
            placeholder="$"
          />
        </div>
      )}

      {alljobs && !sameRate && (
        <div className="mt-8">
          {options?.map((item, index) => {
            const currentRate =
               multiJobType.find((j) => j.job_type === item?.value)?.rate ??
               assignedJobTypes?.find((d: any) => d.job_type === item?.value)?.rate ??
               0;

            return (
              <div key={index} className="flex justify-start w-full space-x-8 py-3">
                <ReactSelect
                  InputDivClass="w-[350px]"
                  label={jobTypeLabel}
                  type="text"
                  placeholder={intl.formatMessage({
                    id: "CLEANER.PROFILE.JOBTYPE.INPUT.PLACEHOLDER",
                    defaultMessage: "Enter Job Type",
                  })}
                  value={item?.value}
                  defaultValue={item}
                  options={[item]}
                />
                <div className="w-[350px] relative">
                  <Input
                    InputDivClass=""
                    topinputdiv="-mt-2"
                    onChange={(e: any) => handleRateChange(e.target.value, item?.value as string)}
                    value={currentRate}
                    name="rate"
                    label={hourlyRateLabel}
                    type="number"
                    placeholder="$"
                  />
                  {multiJobTypeErrors[item.value as string] && (
                    <div className="text-red-600 text-sm absolute -bottom-5">
                      {multiJobTypeErrors[item.value as string]}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-end gap-3 py-3 mt-5">
        <Button 
          outerClass="!w-auto" 
          justify="justify-start" 
          margin="mt-0" 
          onClick={AddAndUpdateJobType} 
          text={<FormattedMessage id="RECRUITER.CONTRACTOR.JOB.TYPE.SAVE" defaultMessage="Save Job Types" />} 
        />
      </div>

      {data?.length > 0 && !(alljobs && !sameRate) && (
        <div className="mt-10 w-full mb-10">
          <div className="flex justify-start w-full gap-8 border-b pb-2">
            <div className="w-[40%] font-bold text-gray-700">Job Type</div>
            <div className="w-[40%] font-bold text-gray-700">Hourly Rate</div>
            <div className="w-[10%] text-right font-bold text-gray-700">Action</div>
          </div>
          {data.map((item, index) => {
            const jobLabel = getJobTypeLabel(item.job_type);
            return (
              <div key={index} className="w-full mt-4 flex justify-start items-center gap-8 py-2 hover:bg-gray-50">
                <div className="text-[14px] font-[500] w-[40%] text-gray-800">
                  {jobLabel || item.job_type}
                </div>
                <div className="text-[14px] font-[500] w-[40%] text-gray-800">
                  ${item.rate}
                </div>
                <div className="w-[10%] flex justify-end">
                  <div
                    onClick={() => handleDeleteAssignedJob(item.id)}
                    className="group cursor-pointer relative rounded-lg inline-flex w-auto items-center border border-neutral-200 py-1.5 px-2 text-black hover:text-[#a9252b] hover:bg-[#FEEEEF] hover:ring-2 hover:ring-[#f76266]"
                  >
                    <Trash2 className="w-8 h-6" strokeWidth={1.5} />
                    <span className="absolute z-10 top-10 right-0 scale-0 rounded bg-gray-800 p-2 text-xs text-white group-hover:scale-100">
                      <FormattedMessage
                        id="ECOMMERCE.COMMON.ICON.TOOLTIP.DELETE"
                        defaultMessage="Delete"
                      />
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
          <Pagination page={page} totalPage={totalPage} setPage={setPage} />
        </div>
      )}

      <div className="pb-40"></div>
    </ContentWithSpinner>
  );
};
